import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { IoCalendarOutline, IoTimeOutline} from "react-icons/io5";
import CustomNavbar from "../../components/common/CustomNavbar";
import PageLayout from "../../components/layouts/PageLayout";
import { whtsappIcon } from "../../asset";
import { AppDispatch } from "../../store";
import { fetchScheduledMessages } from "../../features/template/TemplateService";
import { ScheduledMessage } from "../../types/TemplateType";
import ECSLoader from "../../components/common/EcsLoader";

const STATUS_STYLES: Record<string, string> = {
    PENDING: "bg-yellow-50 text-yellow-700 border-yellow-200",
    SENT:    "bg-green-50 text-green-700 border-green-200",
    FAILED:  "bg-red-50 text-red-700 border-red-200",
};

function parseRequestJson(raw: string) {
    try { return JSON.parse(raw); } catch { return null; }
}

function formatCreatedAt(unix: number) {
    return new Date(unix).toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
}

export default function ScheduledMessages() {
    const dispatch = useDispatch<AppDispatch>();

    const [messages, setMessages] = useState<ScheduledMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const load = async () => {
        try {
            setIsLoading(true);
            const res = await dispatch(fetchScheduledMessages()).unwrap();
            setMessages(res);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    return (
        <PageLayout
            customNavbar={
                <CustomNavbar>
                    <div className="flex gap-3 items-center w-max">
                        <img src={whtsappIcon} className="w-10 h-10" />
                        <span className="font-bold text-lg uppercase">Scheduled Messages</span>
                    </div>
                </CustomNavbar>
            }
        >
            {/* Header bar */}
            <div className="rounded-4xl w-full p-4 flex items-center justify-between">
                <span className="text-sm text-gray-500 font-semibold">
                    {messages.length} scheduled message{messages.length !== 1 ? "s" : ""}
                </span>
            </div>

            {/* Content */}
            <div className="bg-white rounded-4xl p-6 relative">
                <ECSLoader show={isLoading} />

                {messages.length === 0 && !isLoading ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
                        <IoCalendarOutline className="text-5xl" />
                        <span className="text-sm font-semibold">No scheduled messages found</span>
                    </div>
                ) : (
                    <div className="grid grid-cols-3 gap-4">
                        {messages.map((msg) => {
                            const parsed = parseRequestJson(msg.requestJson);
                            const recipients =
                                (parsed?.mobileNumbers?.length || 0) +
                                (parsed?.customerIds?.length || 0);

                            return (
                                <div
                                    key={msg.id}
                                    className="border rounded-2xl p-5 flex flex-col gap-3 hover:shadow-md transition"
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="font-bold text-gray-800 text-sm">
                                            {parsed?.templateName || "—"}
                                        </span>
                                        <span className={`text-xs font-bold px-3 py-1 rounded-full border ${STATUS_STYLES[msg.status] || "bg-gray-50 text-gray-500"}`}>
                                            {msg.status}
                                        </span>
                                    </div>

                                    <div className="flex flex-wrap gap-4 text-xs text-gray-500 font-semibold">
                                        <span className="flex items-center gap-1">
                                            <IoCalendarOutline />
                                            {msg.scheduleDate}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <IoTimeOutline />
                                            {msg.scheduleTime}
                                        </span>
                                        <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                                            {msg.scheduleType}
                                        </span>
                                        {/* <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                                            {parsed?.language || "—"}
                                        </span> */}
                                    </div>

                                    {/* Row 3 — recipients + body params */}
                                    <div className="flex flex-wrap gap-4 text-xs text-gray-400">
                                        {recipients > 0 && (
                                            <span>{recipients} recipient{recipients !== 1 ? "s" : ""}</span>
                                        )}
                                        {/* {parsed?.bodyParams?.length > 0 && (
                                            <span className="truncate max-w-xs">
                                                Params: {parsed.bodyParams.join(", ")}
                                            </span>
                                        )} */}
                                    </div>

                                    {/* Row 4 — created at */}
                                    <div className="text-xs text-gray-300 font-medium">
                                        Created {formatCreatedAt(msg.createdAt)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </PageLayout>
    );
}